import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {TextComponent} cartSubTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#afterRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();

    this.classList.add('m-is_loading');
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal, cartSubTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    cartSubTotal?.shimmer();
    this.classList.add('m-is_loading');

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);
        setTimeout(() => {
          this.classList.remove('m-is_loading');
        }, 300);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        document.dispatchEvent(
          new CustomEvent("free-shipping-threshold:update", {
            detail: {
              sectionId: this.sectionId,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            }
          })
        );

        this.#afterRender();

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  //Newly added functions
  #afterRender() {
    this.#autoCorrectPartial();
    this.#disablePartialIncrease();
    this.#handleCheckoutProtection();
    this.#handleSoldOutItems();
    this.#initBackInStockForms();
  }

  #initBackInStockForms() {
    this.querySelectorAll('.cart-items__notify-button').forEach((button) => {
      button.removeEventListener('click', this.#handleBackInStockSubmit);
      button.addEventListener('click', this.#handleBackInStockSubmit.bind(this));
    });
  }

  async #handleBackInStockSubmit(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const notifyDiv = button.closest('.cart-items__notify');
    const emailInput = notifyDiv.querySelector('.cart-items__notify-input');
    const email = emailInput.value.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showToast('Please enter a valid email address');
      return;
    }

    const variantId = notifyDiv.dataset.variantId;
    const companyId = Theme?.otterVars?.klaviyoApiKey;

    if (!companyId) {
      console.error('Klaviyo public API key not configured');
      this.#showToast('Something went wrong. Please try again.');
      return;
    }

    const notifyMeText = button.dataset.notifyMeText || 'Notify Me';
    button.disabled = true;
    button.textContent = 'Sending...';

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2025-01-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 202 || response.ok) {
        const formEl = notifyDiv.querySelector('.cart-items__notify-form');
        const successEl = notifyDiv.querySelector('.cart-items__notify-success');
        if (formEl) formEl.classList.add('hidden');
        if (successEl) successEl.classList.remove('hidden');
      } else {
        const errorMsg = responseData?.errors?.[0]?.detail || `API error: ${response.status}`;
        console.error('Klaviyo BIS API error:', errorMsg);
        this.#showToast('Something went wrong. Please try again.');
        button.disabled = false;
        button.textContent = notifyMeText;
      }
    } catch (error) {
      console.error('BIS submit error:', error);
      this.#showToast('Something went wrong. Please try again.');
      button.disabled = false;
      button.textContent = notifyMeText;
    }
  }

  //PARTIAL STOCK AUTO CORRECTION
  #autoCorrectPartial() {
    const partialRows = this.querySelectorAll('[data-partial="true"]');

    partialRows.forEach((row) => {
      const line = parseInt(row.dataset.line, 10);
      const available = parseInt(row.dataset.available, 10);
      const requested = parseInt(row.dataset.requested, 10);

      if (!line || !available || requested <= available) return;

      this.updateQuantity({
        line,
        quantity: available,
        action: 'change',
      });

      this.#showToast(`Quantity updated: We only have ${available} left!`);
    });
  }

  // Disable "+" Button
  #disablePartialIncrease() {
    const partialRows = this.querySelectorAll('[data-partial="true"]');

    partialRows.forEach(row => {
      const selector = row.querySelector('cart-quantity-selector-component');
      if (!selector) return;

      const plusBtn = selector.querySelector('[data-action="increase"]');
      if (plusBtn) plusBtn.disabled = true;
    });
  }

  // HANDLE CHECKOUT PROTECTION
  #handleCheckoutProtection() {
    const checkoutBtn = document.querySelector('[name="checkout"]');
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener('click', (e) => {
      const soldOutRows = this.querySelectorAll('[data-sold-out="true"]');

      if (!soldOutRows.length) return;

      e.preventDefault();

      const updates = {};
      soldOutRows.forEach(row => {
        updates[row.dataset.line] = 0;
      });

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      }).then(() => {
        window.location.href = '/checkout';
      });
    });
  }

  // TOAST MESSAGE DISPLAY
  #showToast(message) {
    // Remove existing toast if present (prevents stacking)
    const existing = document.querySelector('.cart-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.textContent = message;

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Auto remove
    setTimeout(() => {
      toast.classList.remove('is-visible');

      setTimeout(() => {
        toast.remove();
      }, 200);
    }, 3500);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Sold out items Notice
   */
  #handleSoldOutItems = () => {
    const cartItemContainers = document.querySelectorAll('[ref="cartItemRows[]"]');
    if(!cartItemContainers.length) return;
    cartItemContainers.forEach(cartItemContainer => {
      const line = Number(cartItemContainer.dataset.line);
      const isSoldOut = cartItemContainer.dataset.soldOut === "true";
      const soldOutError = cartItemContainer.dataset.soldOutError || "This product is no longer available";
      if(isSoldOut) {
        this.#handleCartError(line, { errors: soldOutError })
      }
    });
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      document.dispatchEvent(
        new CustomEvent("free-shipping-threshold:update", event)
      );
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      document.dispatchEvent(
        new CustomEvent("free-shipping-threshold:update", event)
      );

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    document.querySelector('cart-items-component')
    ? document.querySelector('cart-items-component')?.classList.add('is-loading')
    : "";
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    document.querySelector('cart-items-component')
    ? document.querySelector('cart-items-component')?.classList.remove('is-loading')
    : "";
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
